/** Messenger-style chat dock: conversation list (online row on top, most
 *  recent first, unread = bold + blue dot), a thread view, and the new-chat
 *  sheet with group creation. Docked bottom-right on desktop, full-screen on
 *  phones. Polls while open; the top-bar badge polls on its own. */
import { useEffect, useMemo, useRef, useState } from "react";
import { Bot, Check, Loader2, Minus, PencilLine, Search, Trash2, Users, X } from "lucide-react";
import { toast } from "sonner";
import Avatar from "./Avatar";
import BotInboxView from "./BotInboxView";
import ChatThread from "./ChatThread";
import { createConversation, deleteConversation, fetchConversations, fetchPeople, isChatAdmin } from "./chatApi";

const relTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 7 * 86400) return d.toLocaleDateString("en-CA", { weekday: "short" });
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
};

function GroupAvatar({ members }) {
  const shown = members.slice(0, 2);
  return (
    <div className="relative h-10 w-10 shrink-0">
      {shown.map((m, i) => (
        <div key={m.user_id} className={`absolute ${i === 0 ? "left-0 top-0" : "bottom-0 right-0"}`} style={{ zIndex: i }}>
          <Avatar name={m.name} picture={m.picture} showStatus={false} size={shown.length > 1 ? 26 : 40} />
        </div>
      ))}
      {members.length === 0 && <div className="h-10 w-10 rounded-full bg-gray-200" />}
    </div>
  );
}

function NewChatView({ people, meId, isAdmin, startInGroupMode, onCreated, onCancel }) {
  const [q, setQ] = useState("");
  const [groupMode, setGroupMode] = useState(!!startInGroupMode);
  const [selected, setSelected] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [busy, setBusy] = useState(false);
  const others = people.filter((p) => p.user_id !== meId);
  const filtered = others.filter((p) => (p.name || "").toLowerCase().includes(q.toLowerCase()));

  const toggle = (uid) => setSelected((s) => (s.includes(uid) ? s.filter((x) => x !== uid) : [...s, uid]));

  const openDirect = async (uid) => {
    if (busy) return;
    setBusy(true);
    try {
      onCreated(await createConversation([uid]));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the chat");
      setBusy(false);
    }
  };

  const createGroup = async () => {
    if (!selected.length || busy) return;
    setBusy(true);
    try {
      onCreated(await createConversation(selected, groupName.trim(), true));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the group");
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="chat-new">
      <div className="flex shrink-0 items-center gap-2 border-b border-gray-200 px-3 py-2.5">
        <button onClick={onCancel} data-testid="chat-new-cancel" aria-label="Back" className="rounded-full p-1.5 text-gray-500 hover:bg-gray-100">
          <X className="h-4 w-4" />
        </button>
        <p className="flex-1 text-sm font-semibold text-gray-900">{groupMode ? "New group chat" : "New message"}</p>
        {groupMode ? (
          <button
            onClick={createGroup}
            disabled={!selected.length || busy}
            data-testid="chat-new-create"
            className="rounded-full bg-[#1a3fd1] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#1633b8] disabled:opacity-40"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : `Create group${selected.length ? ` (${selected.length})` : ""}`}
          </button>
        ) : null}
      </div>
      {/* admins get the group path; everyone else just taps a person */}
      {isAdmin && !groupMode && (
        <button onClick={() => setGroupMode(true)} data-testid="chat-new-group-switch"
          className="flex w-full shrink-0 items-center gap-3 border-b border-gray-100 px-3 py-2.5 text-left hover:bg-gray-50">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1a3fd1]/10 text-[#1a3fd1]">
            <Users className="h-4 w-4" />
          </span>
          <span className="text-sm font-semibold text-[#1a3fd1]">Create a group chat</span>
        </button>
      )}
      <div className="shrink-0 border-b border-gray-100 p-2">
        <div className="flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5">
          <Search className="h-3.5 w-3.5 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people" data-testid="chat-new-search"
            className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 focus:outline-none" />
        </div>
      </div>
      {groupMode && (
        <div className="shrink-0 border-b border-gray-100 px-3 py-2">
          <input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Group name (optional)" data-testid="chat-group-name"
            className="w-full rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-900 focus:border-[#1a3fd1] focus:outline-none" />
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {filtered.map((p) => {
          const on = selected.includes(p.user_id);
          return (
            <button key={p.user_id} onClick={() => (groupMode ? toggle(p.user_id) : openDirect(p.user_id))}
              data-testid={`chat-new-person-${p.user_id}`}
              className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-gray-50">
              <Avatar name={p.name} picture={p.picture} online={p.online} lastActive={p.last_active} size={38} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">{p.name}</p>
                <p className="text-[11px] text-gray-400">{p.online ? "Active now" : "Offline"}</p>
              </div>
              {groupMode && (
                <span className={`flex h-5 w-5 items-center justify-center rounded-full border ${on ? "border-[#1a3fd1] bg-[#1a3fd1]" : "border-gray-300"}`}>
                  {on && <Check className="h-3 w-3 text-white" />}
                </span>
              )}
            </button>
          );
        })}
        {filtered.length === 0 && <p className="py-8 text-center text-xs text-gray-400">No one by that name.</p>}
      </div>
    </div>
  );
}

const PresenceBubble = ({ p, testId, onOpen }) => (
  <button className="flex w-12 shrink-0 flex-col items-center gap-1" data-testid={testId} onClick={() => onOpen(p)}>
    <Avatar name={p.name} picture={p.picture} online={p.online} lastActive={p.last_active} size={44} />
    <span className="w-full truncate text-center text-[10px] text-gray-500">{(p.name || "").split(" ")[0]}</span>
  </button>
);

export default function ChatPanel({ me, onClose, onSeen, minimized, onMinimize, onExpand, openConvoId, onOpenHandled }) {
  const [people, setPeople] = useState([]);
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [newChat, setNewChat] = useState(null); // null | "direct" | "group"
  const [confirmDelete, setConfirmDelete] = useState(null); // conversation_id awaiting confirm
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [botView, setBotView] = useState(false); // superadmin: read the bot's inbox as the bot
  const isAdmin = isChatAdmin(me.role);
  const seenRef = useRef(onSeen);
  seenRef.current = onSeen;

  const loadConvos = async () => {
    try {
      const list = await fetchConversations();
      setConvos(list);
      seenRef.current?.(list.reduce((s, c) => s + (c.unread_count ? 1 : 0), 0));
      setActive((a) => {
        if (!a) return a;
        return list.find((c) => c.conversation_id === a.conversation_id) || a;
      });
    } catch { /* transient */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [p] = await Promise.all([fetchPeople(), loadConvos()]);
        setPeople(p);
      } catch {
        toast.error("Chat could not load");
      } finally {
        setLoading(false);
      }
    })();
    const t = setInterval(loadConvos, 4000);
    const p = setInterval(() => fetchPeople().then(setPeople).catch(() => {}), 20000);
    return () => { clearInterval(t); clearInterval(p); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(
    () => convos.filter((c) => (c.name || "").toLowerCase().includes(q.toLowerCase())),
    [convos, q]
  );
  // Online strip: never show yourself, and never show superadmin accounts —
  // the boss's bubble isn't anyone else's quick-dial (they can still open a
  // chat with them from the conversation list or new-chat picker).
  const BOT_ID = "user_elitebot";
  const botPerson = people.find((p) => p.user_id === BOT_ID) || null;
  const onlinePeople = people.filter((p) => p.user_id !== me.user_id && p.role !== "superadmin" && p.user_id !== BOT_ID);
  const openDm = async (p) => {
    try {
      const convo = await createConversation([p.user_id]);
      setActive(convo);
    } catch { toast.error("Could not open the chat"); }
  };

  // A notification/push asked us to jump straight into a conversation.
  useEffect(() => {
    if (!openConvoId || loading) return;
    const c = convos.find((x) => x.conversation_id === openConvoId);
    if (c) {
      setNewChat(null);
      setActive(c);
    } else {
      toast.error("That chat isn't available anymore");
    }
    onOpenHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openConvoId, loading]);

  if (minimized) {
    // Messenger-style floating bar. The whole component (thread state
    // included) stays mounted, so expanding picks up exactly where you left
    // off — and it persists across tab/area changes.
    const unreadTotal = convos.reduce((s, c) => s + (c.unread_count || 0), 0);
    return (
      <div className="flex w-full items-center gap-2 rounded-t-xl border border-gray-200 bg-white px-3 py-2 shadow-2xl sm:border-b-0" data-testid="chat-minibar">
        <button onClick={onExpand} className="flex min-w-0 flex-1 items-center gap-2 text-left" data-testid="chat-minibar-expand" aria-label="Expand chat">
          {active && <Avatar name={active.name} picture={active.picture} online={active.online} size={28} />}
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
            {active ? active.name : "Chats"}
          </span>
          {unreadTotal > 0 && (
            <span className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-[#1a8cff] px-1 text-[9px] font-bold text-white" data-testid="chat-minibar-unread">
              {unreadTotal > 99 ? "99+" : unreadTotal}
            </span>
          )}
        </button>
        <button onClick={onClose} data-testid="chat-minibar-close" aria-label="Close chat"
          className="shrink-0 rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  if (botView) {
    return <BotInboxView onExit={() => setBotView(false)} onMinimize={onMinimize} onClose={onClose} />;
  }
  if (active) {
    return (
      <ChatThread
        me={me.user_id}
        convo={active}
        people={people}
        isAdmin={isAdmin}
        onBack={() => { setActive(null); loadConvos(); }}
        onMinimize={onMinimize}
        onClose={onClose}
      />
    );
  }
  if (newChat) {
    return (
      <NewChatView
        people={people}
        meId={me.user_id}
        isAdmin={isAdmin}
        startInGroupMode={newChat === "group"}
        onCancel={() => setNewChat(null)}
        onCreated={(convo) => { setNewChat(null); setActive(convo); }}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="chat-list">
      {/* header */}
      <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-3">
        <h2 className="text-lg font-bold text-gray-900">Chats</h2>
        <div className="flex items-center gap-1">
          {["superadmin", "owner", "admin"].includes(me.role) && (
            <button onClick={() => setBotView(true)} data-testid="chat-view-as-bot-btn" aria-label="View chats as EliteBot" title="View as EliteBot — read what people ask the bot"
              className="rounded-full bg-amber-100 p-2 text-amber-700 hover:bg-amber-200">
              <Bot className="h-4 w-4" />
            </button>
          )}
          {isAdmin && (
            <button onClick={() => setNewChat("group")} data-testid="chat-new-group-btn" aria-label="New group chat" title="New group chat"
              className="rounded-full bg-gray-100 p-2 text-gray-600 hover:bg-gray-200">
              <Users className="h-4 w-4" />
            </button>
          )}
          <button onClick={() => setNewChat("direct")} data-testid="chat-new-btn" aria-label="New message"
            className="rounded-full bg-gray-100 p-2 text-gray-600 hover:bg-gray-200">
            <PencilLine className="h-4 w-4" />
          </button>
          <button onClick={onMinimize} data-testid="chat-minimize-btn" aria-label="Minimize chat" title="Minimize"
            className="rounded-full bg-gray-100 p-2 text-gray-600 hover:bg-gray-200">
            <Minus className="h-4 w-4" />
          </button>
          <button onClick={onClose} data-testid="chat-close-btn" aria-label="Close chat"
            className="rounded-full bg-gray-100 p-2 text-gray-600 hover:bg-gray-200">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* presence row — the bot pinned far left (always online), a divider, then everyone else online-first */}
      <div className="flex shrink-0 items-start gap-3 overflow-x-auto px-4 py-3" data-testid="chat-online-row">
        {botPerson && (
          <>
            <PresenceBubble p={{ ...botPerson, online: true }} testId="chat-online-bot" onOpen={openDm} />
            <span className="mx-0.5 h-11 w-px shrink-0 self-start bg-gray-300" aria-hidden="true" data-testid="chat-online-divider" />
          </>
        )}
        {onlinePeople.map((p) => <PresenceBubble key={p.user_id} p={p} testId={`chat-online-${p.user_id}`} onOpen={openDm} />)}
        {onlinePeople.length === 0 && !botPerson && !loading && <p className="text-xs text-gray-400">No one else here yet.</p>}
      </div>

      {/* search */}
      <div className="shrink-0 px-4 pb-2">
        <div className="flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5">
          <Search className="h-3.5 w-3.5 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search chats" data-testid="chat-search"
            className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 focus:outline-none" />
        </div>
      </div>

      {/* conversation list — most recent first; safe-area pad so the last row
          isn't hidden under the iPhone home bar */}
      <div className="min-h-0 flex-1 overflow-y-auto pb-[max(env(safe-area-inset-bottom),0.5rem)]">
        {loading && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-gray-300" /></div>}
        {!loading && filtered.length === 0 && (
          <p className="px-6 py-10 text-center text-xs text-gray-400">
            No chats yet. Tap a face above or the pencil to start one.
          </p>
        )}
        {filtered.map((c) => {
          const unread = c.unread_count > 0;
          const previewPrefix = c.last_sender_id === me.user_id ? "You: " : (c.is_group && c.last_sender_name ? `${c.last_sender_name.split(" ")[0]}: ` : "");
          if (confirmDelete === c.conversation_id) {
            return (
              <div key={c.conversation_id} className="flex items-center gap-2 border-l-2 border-red-400 bg-red-50/60 px-4 py-3" data-testid={`chat-delete-confirm-${c.conversation_id}`}>
                <p className="min-w-0 flex-1 text-xs text-gray-600">
                  Delete the whole thread with <b>{c.name}</b>? It only disappears for you.
                </p>
                <button
                  onClick={async () => {
                    try {
                      await deleteConversation(c.conversation_id);
                      setConfirmDelete(null);
                      loadConvos();
                    } catch { toast.error("Could not delete the thread"); }
                  }}
                  data-testid={`chat-delete-yes-${c.conversation_id}`}
                  className="shrink-0 rounded-full bg-red-500 px-3 py-1 text-[11px] font-semibold text-white hover:bg-red-600"
                >
                  Delete
                </button>
                <button onClick={() => setConfirmDelete(null)} data-testid={`chat-delete-no-${c.conversation_id}`}
                  className="shrink-0 rounded-full bg-gray-200 px-3 py-1 text-[11px] font-semibold text-gray-600">
                  Cancel
                </button>
              </div>
            );
          }
          return (
            <div key={c.conversation_id} className="group relative">
              <button onClick={() => setActive(c)} data-testid={`chat-convo-${c.conversation_id}`}
                className="flex w-full items-center gap-3 px-4 py-2.5 pr-9 text-left hover:bg-gray-50">
                {c.is_group ? <GroupAvatar members={c.members.filter((m) => m.user_id !== me.user_id)} /> : (
                  <Avatar name={c.name} picture={c.picture} online={c.online} lastActive={c.peer_last_active} size={44} />
                )}
                <div className="min-w-0 flex-1">
                  <p className={`truncate text-sm ${unread ? "font-bold text-gray-900" : "font-medium text-gray-800"}`}>{c.name}</p>
                  <p className={`truncate text-xs ${unread ? "font-semibold text-gray-900" : "text-gray-500"}`}>
                    {c.last_message_preview ? `${previewPrefix}${c.last_message_preview}` : "Started just now"}
                    <span className="text-gray-400"> · {relTime(c.last_message_at)}</span>
                    {unread && <span className="ml-1.5 inline-block h-2 w-2 rounded-full bg-[#1a8cff] align-middle" data-testid={`chat-unread-dot-${c.conversation_id}`} />}
                  </p>
                </div>
              </button>
              <button onClick={() => setConfirmDelete(c.conversation_id)} data-testid={`chat-delete-${c.conversation_id}`}
                aria-label="Delete conversation" title="Delete thread"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-gray-300 transition-colors hover:bg-red-50 hover:text-red-500 sm:opacity-0 sm:group-hover:opacity-100">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
